import { Loader2, Plus, Trash2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DatePickerCell } from '@/components/common/DatePickerCell'
import { cn } from '@/utils/cn'
import type { WaveRow } from './types'

export interface DCOption {
  id: string
  name: string
}

interface WaveBreakTableProps {
  isAllDCs: boolean
  distributionType: 'percentage' | 'absolute'
  rows: WaveRow[]
  onAddRow: () => void
  onDeleteRow: (index: number) => void
  onUpdateRow: (index: number, field: keyof WaveRow, value: unknown) => void
  onRemove?: () => void
  selectedDC?: string
  onDCChange?: (value: string) => void
  usedDCs?: string[]
  dcOptions: DCOption[]
  isLoadingDCs: boolean
  showErrors?: boolean
  allDcsSentinel?: string
  hasStoreModel?: boolean
}

export function WaveBreakTable({
  isAllDCs,
  distributionType,
  rows,
  onAddRow,
  onDeleteRow,
  onUpdateRow,
  onRemove,
  selectedDC,
  onDCChange,
  usedDCs = [],
  dcOptions,
  isLoadingDCs,
  showErrors,
  allDcsSentinel,
  hasStoreModel = true,
}: WaveBreakTableProps) {
  const availableDCs = dcOptions.filter(
    dc => dc.id === selectedDC || !usedDCs.includes(dc.id)
  )
  const canSelectAllDCs =
    allDcsSentinel &&
    hasStoreModel &&
    (isAllDCs || !usedDCs.includes(allDcsSentinel))
  const isPercentage = distributionType === 'percentage'
  const totalPercentage = isPercentage
    ? rows.reduce((sum, row) => sum + (row.percent ?? 0), 0)
    : null

  const hasPercentageError = isPercentage && totalPercentage !== 100

  return (
    <div className="rounded-md border border-[#e7e7e7]">
      {/* Table header section */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#e7e7e7] bg-[#fafafa]">
        {isLoadingDCs ? (
          <div className="flex items-center gap-2 h-8 px-3">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Loading DCs…</span>
          </div>
        ) : (
          <Select value={selectedDC || undefined} onValueChange={onDCChange}>
            <SelectTrigger className="w-55 h-8 text-sm">
              <SelectValue placeholder="Select Distribution Center" />
            </SelectTrigger>
            <SelectContent>
              {canSelectAllDCs && (
                <SelectItem value={allDcsSentinel!}>
                  All Distribution Centers
                </SelectItem>
              )}
              {availableDCs.map(dc => (
                <SelectItem key={dc.id} value={dc.id}>
                  {dc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <div className="flex items-center gap-2">
          {isPercentage && rows.length > 0 && (
            <span
              className={cn(
                'text-xs font-medium',
                hasPercentageError ? 'text-[#CF1F2E]' : 'text-green-600'
              )}
            >
              Total: {totalPercentage}%
              {hasPercentageError && ' \u26A0 Must equal 100%'}
            </span>
          )}
          {onRemove && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 cursor-pointer"
              onClick={onRemove}
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#e7e7e7] bg-[#fafafa]">
            <th className="text-left px-4 py-2 font-semibold text-muted-foreground w-15">
              {isPercentage ? 'Flow #' : 'Wave #'}
            </th>
            <th className="text-left px-4 py-2 font-semibold text-muted-foreground">
              {isPercentage ? 'Qty % (preview)*' : 'Qty *'}
            </th>
            <th className="text-left px-4 py-2 font-semibold text-muted-foreground">
              Ship Date
            </th>
            <th className="text-left px-4 py-2 font-semibold text-muted-foreground">
              Receive Date
            </th>
            <th className="w-11" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={row.id ?? `new-${row.waveNumber}`}
              className="border-b border-[#e7e7e7] last:border-0"
            >
              <td className="px-4 py-2 text-muted-foreground">
                {row.waveNumber}
              </td>
              <td className="px-4 py-2">
                <div className="relative">
                  <Input
                    type="number"
                    value={
                      isPercentage ? (row.percent ?? '') : (row.quantity ?? '')
                    }
                    onChange={e => {
                      if (e.target.value === '') {
                        onUpdateRow(
                          index,
                          isPercentage ? 'percent' : 'quantity',
                          null
                        )
                        return
                      }
                      let val = Number(e.target.value)
                      if (val < 0) val = 0
                      if (isPercentage && val > 100) val = 100
                      onUpdateRow(
                        index,
                        isPercentage ? 'percent' : 'quantity',
                        val
                      )
                    }}
                    className={cn(
                      'h-8 w-25 text-sm pr-6',
                      showErrors &&
                        (isPercentage ? row.percent : row.quantity) === null &&
                        'border-red-500'
                    )}
                    min={0}
                    max={isPercentage ? 100 : undefined}
                  />
                  {isPercentage && (
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none">
                      %
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-2">
                <DatePickerCell
                  value={row.shipDate}
                  onChange={date => onUpdateRow(index, 'shipDate', date)}
                />
              </td>
              <td className="px-4 py-2">
                <DatePickerCell
                  value={row.receiveDate}
                  onChange={date => onUpdateRow(index, 'receiveDate', date)}
                />
              </td>
              <td className="px-2 py-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 cursor-pointer"
                  onClick={() => onDeleteRow(index)}
                >
                  <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Add row button */}
      <div className="px-4 py-2 border-t border-[#e7e7e7]">
        <button
          onClick={onAddRow}
          className="flex items-center gap-1.5 text-sm font-medium text-[#CF1F2E] hover:text-[#CF1F2E]/80 cursor-pointer"
        >
          <Plus className="h-4 w-4" />
          Add Row
        </button>
      </div>
    </div>
  )
}
